import { contractsService, fleetService, airportService, db, maintenanceService } from './firestore';
import { archiveService } from './archiveService';
import { modelPricingService } from './modelPricingService';
import { collection, doc, getDocs, setDoc, query, where, writeBatch } from 'firebase/firestore';
import { toISOLocalDateTime } from '../utils/contractDateUtils';
import logger from '../utils/logger';
import { inFleet } from './fleetStatus';
import { isInvygo, normalizeBranch, parseContractDate } from '../pages/KPIModule/kpi.logic';
import { periodKind, businessBayKindOf } from './contractRules';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';

const SETTINGS_COLLECTION = 'kpi_settings';
const STAFF_MAPPINGS_DOC = 'staff_mappings';
const STAFF_BRANCHES_DOC = 'staff_branches';
const STAFF_ROLES_DOC = 'staff_roles';
const STAFF_NOS_DOC = 'staff_nos';
const STAFF_EXCLUDED_DOC = 'staff_excluded';   // names not counted as employees: their contracts count for the branch

// AUTHORIZED_STAFF removed - now dynamic based on database mappings

// With the new store (USE_CONTRACTS_V2) the KPI settings are the engine's office records (/api/office/kpi-settings):
// the same documents by the same keys. A target is merged into its document (as setDoc merge: true); the five
// staff_* documents are replaced whole (as setDoc without merge). The store stamps last_updated itself.
const storeKpiDoc = async (key) => (await finesService.officeKpi(key).catch((e) => { throw officeError(e); }))?.data || {};
const storeKpiSave = (key, data, merge) => finesService.officeKpiSave(key, data, { merge }).catch((e) => { throw officeError(e); });

// Helper for Quarter Logic
const getQuarter = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const month = date.getMonth();
    return `Q${Math.floor(month / 3) + 1}`;
};

// Branch to City Mapping
const BRANCH_TO_CITY = (branchName) => {
    if (!branchName) return 'Other';
    const name = branchName.toUpperCase();
    if (
        name.includes('DXB') || name.includes('DUBAI') || name.includes('AIRPORT') || 
        name.includes('BUSINESS BAY') || name.includes('TERMINAL') || name.includes('TERMENAL') ||
        name.includes('T1') || name.includes('T2')
    ) return 'Dubai';
    if (name.includes('SHJ') || name.includes('SHARJAH')) return 'Sharjah';
    if (name.includes('MUSCAT') || name.includes('OMAN')) return 'Muscat';
    if (name.includes('AUH') || name.includes('ABU DHABI')) return 'Abu Dhabi';
    return 'Other';
};

const NORMALIZE_BRANCH = normalizeBranch;

const GET_CONTRACT_TYPE = (bookingNo, contractNo, start, end, branchName, rawBranch = '', row = null) => {
    const b = (bookingNo || '').toString().toLowerCase();

    // Branch code D04 in the Ejar number (B2609000D04…) = Business Bay - Invygo:
    // an Invygo subscription car whatever the booking number or duration says.
    if (isInvygo(contractNo, bookingNo, rawBranch || branchName)) return 'Invygo';

    const isMonthlyKw = b.includes('monthly');
    const isDailyKw = b.includes('daily') || b.includes('day') || b.includes('rent');

    if (isMonthlyKw) return 'Monthly';
    if (isDailyKw) return 'Daily';

    // Business Bay: the engine's own Daily / Monthly when the row carries it (owner, 14 Oct 2026)
    const bayKind = businessBayKindOf(row, contractNo);
    if (bayKind) return bayKind;
    // 3. The period (start / end already read by parseContractDate): monthly from 30 days - the app's one
    // rule (contractRules). It was < 25 Daily, 28+ Monthly and 25-27 fell through to Daily (DUP-01).
    return periodKind(start, end) || 'Daily';
};

// Staff Name Mapping (Arabic -> English)
const NORMALIZE_STAFF_NAME = (name, customMappings = {}) => {
    if (!name) return 'System';
    const s = name.toString().trim();
    
    // Normalize Arabic helper: standardize Alef, Taa Marbuta, and Alef Maqsura
    const norm = (str) => (str || '')
        .replace(/[أإآ]/g, 'ا')
        .replace(/[ى]/g, 'ي')
        .replace(/ة/g, 'ه')
        .replace(/\s+/g, ' ')
        .trim();

    const inputLow = norm(s);
    
    // 1. Check Custom Mappings first
    // customMappings: { "Display Name": ["Real Name 1", "Real Name 2"] }
    for (const [displayName, realNames] of Object.entries(customMappings)) {
        if (Array.isArray(realNames)) {
            if (realNames.some(rn => norm(rn) === inputLow)) return displayName;
        }
    }

    // 2. Remove Hardcoded mappings - now handled via DB mapping tab in settings
    
    return s;
};

// Status Mapping
const RAW_STATUS_MAP = (status) => {
    const s = (status || '').toString().trim().toLowerCase();
    if (s.includes('delivered') || s.includes('closed') || s === 'finished') return 'Closed';
    if (s.includes('cancelled') || s.includes('void') || s.includes('deleted') || s.includes('rejected') || s.includes('no-show')) return 'Cancelled';
    if (s.includes('in-progress') || s.includes('open') || s.includes('pickup') || s.includes('on rent')) return 'In-Progress';
    return 'In-Progress';
};

export const kpiService = {
    /**
     * Aggregates contracts into a Unified Operations View
     */
    // `since` ("yyyy-mm-dd"): with the contracts store (USE_CONTRACTS_V2) the contracts open now or returned since that
    // day - what the chosen period, the one before it and the cars on the road need; the store's contracts carry
    // their own dates and cars, so the E-Check archive is not read to fill them. Firestore: every contract, as before.
    getUnifiedOpsView: async (forceRefresh = false, { since } = {}) => {
        try {
            const [regularContractsRaw, airportContractsRaw, archiveRaw, fleet, modelPricing, staffMappings] = await Promise.all([
                contractsService.getAllContracts(forceRefresh, { since }),
                airportService.getAllAirportContracts(forceRefresh, { since }),
                USE_CONTRACTS_V2 ? [] : archiveService.load(),
                fleetService.getAllCars(forceRefresh), // Fetch fleet for fallback rates
                modelPricingService.getAllPricing(), // Fetch Standard Pricing
                kpiService.getStaffMappings()
            ]);

            const regularContracts = regularContractsRaw.map(c => ({ ...c, __source: 'regular' }));
            const airportContracts = airportContractsRaw.map(c => ({ ...c, __source: 'airport' }));
            
            // --- ARCHIVE (E-Check log) ---
            // One record per Create / Close check. A monthly contract that changed cars has several of each, so a
            // contract's archive dates are its FIRST Create (pickup) and LAST Close (return); the latest check gives
            // the car. The contract's own Ejar data (regular / airport collections) always wins: 7,145 contracts had
            // their start moved to a later car change when the archive overrode it (25 Sep 2026). The archive only
            // fills what a contract lacks, and stands alone for contracts not in the collections.
            const archiveGroups = new Map();
            archiveRaw.forEach(c => {
                const cNo = c['Contract No.'] || c['Agreement'] || c['Contract No'];
                if (!cNo) return;
                const key = cNo.toString().toUpperCase();
                const at = parseContractDate(c['Transaction Date']);
                if (!at) return;
                const g = archiveGroups.get(key) || { firstCreate: null, lastCreate: null, lastClose: null, last: null, lastAt: null, row: c };
                const type = (c['Check Type'] || '').toString().toLowerCase();
                if (type.includes('create') || type.includes('pick')) {
                    if (!g.firstCreate || at < g.firstCreate.at) g.firstCreate = { at, text: c['Transaction Date'] };
                    if (!g.lastCreate || at > g.lastCreate) g.lastCreate = at;
                } else if (type.includes('close') || type.includes('drop') || type.includes('return')) {
                    if (!g.lastClose || at > g.lastClose.at) g.lastClose = { at, text: c['Transaction Date'] };
                }
                if (!g.lastAt || at >= g.lastAt) { g.lastAt = at; g.row = c; }
                archiveGroups.set(key, g);
            });
            const archiveOf = (key) => {
                const g = archiveGroups.get(String(key || '').toUpperCase());
                if (!g) return null;
                const closed = g.lastClose && (!g.lastCreate || g.lastClose.at >= g.lastCreate);
                return {
                    pickup: g.firstCreate?.text || null,
                    dropoff: g.lastClose?.text || null,
                    status: closed ? 'Closed' : 'In-Progress',
                    plate: g.row['Plate Number'] || g.row['Plate No.'] || '',
                    branch: g.row['Branch'] || '',
                    model: g.row['Model'] || '',
                };
            };

            // Regular + airport, one row per contract number
            const contractsMap = new Map();
            [...airportContracts, ...regularContracts].forEach(c => {
                const key = String(c['Contract No.'] || c.contract_no || c.Agreement || c.AgreementNo || c.id || '').toUpperCase();
                if (!key) return;
                contractsMap.set(key, contractsMap.has(key) ? { ...contractsMap.get(key), ...c } : c);
            });
            // The archive fills missing dates / car; contracts only in the archive stand on their own
            contractsMap.forEach((c, key) => {
                const a = archiveOf(key);
                if (!a) return;
                const own = (v) => v != null && String(v).trim() !== '';
                if (!own(c.pickup_date) && !own(c['Pick-up Date']) && a.pickup) c.pickup_date = a.pickup;
                if (!own(c.dropoff_date) && !own(c['Drop-off Date']) && a.dropoff) c.dropoff_date = a.dropoff;
                if (!own(c.plate_no) && !own(c['Plate No.']) && a.plate) c.plate_no = a.plate;
                if (!own(c.pickup_branch) && !own(c['Pick-up Branch']) && a.branch) c.pickup_branch = a.branch;
                if (!own(c.status) && !own(c.Status)) c.status = a.status;
            });
            archiveGroups.forEach((g, key) => {
                if (contractsMap.has(key)) return;
                const a = archiveOf(key);
                contractsMap.set(key, {
                    __source: 'archive', contract_no: key, pickup_date: a.pickup, dropoff_date: a.dropoff,
                    plate_no: a.plate, Branch: a.branch, model: a.model, status: a.status,
                });
            });

            const contracts = Array.from(contractsMap.values());
            
            // Create Fleet Rate, Year & Model Maps
            const fleetRateMap = {};
            const fleetYearMap = {};
            const fleetModelMap = {};
            
            if (fleet && fleet.length > 0) {
                fleet.forEach(car => {
                    const plate = (car.plate_number || car.plateNo || '').trim().replace(/\s+/g, '');
                    if (plate) {
                        if (car.rental_rate) fleetRateMap[plate] = Number(car.rental_rate);
                        if (car.year || car.Year) fleetYearMap[plate] = (car.year || car.Year).toString().trim();
                        
                        // normalize model name from fleet
                        const model = car.model_name || car.model;
                        if (model) fleetModelMap[plate] = model.toString().trim().toUpperCase();
                    }
                });
            }

            // Create Model Pricing Map (Directly use fetched map)
            const modelPricingMap = modelPricing || {};

            if (!contracts || contracts.length === 0) return [];

            // 2. Process & Normalize
            return contracts.map(c => {
                const fullContractNo = (c['Contract No.'] || c.contract_no || c.Agreement || c.AgreementNo || '').toString();
                // The date's format comes from its text (engine: DD/MM/YYYY HH:MM; old airport: M/D/YYYY h:mm:ss AM)
                const rawPickupDate = c.pickup_date || c['Pick-up Date'] || c.PickupDate;
                const dateObj = parseContractDate(rawPickupDate);
                const pickupDate = dateObj ? toISOLocalDateTime(dateObj) : null;
                const rawDropoffDate = c.dropoff_date || c['Drop-off Date'] || c.DropoffDate;
                const dDateObj = parseContractDate(rawDropoffDate);
                const dropoffDate = dDateObj ? toISOLocalDateTime(dDateObj) : null;

                const branch = c['Pick-up Branch'] || c.pickup_branch || c['Branch'] || c.branch;
                const normalizedBranch = NORMALIZE_BRANCH(branch);
                const bookingNo = c['Booking Number'] || c.booking_number || c.BookingNo;
                // No status at all (a stub row, nothing in the archive): open only while its return is still ahead
                const statusRaw = c['Status'] || c.status || c.ContractStatus
                    || (dDateObj && dDateObj.getTime() < Date.now() ? 'Closed' : '');
                
                const plateRaw = (c['Plate No.'] || c.plate_no || '');
                const plateKey = plateRaw.trim().replace(/\s+/g, ''); // For map lookup
                
                // Rate Logic: Contract Rate > Fleet Standard Rate > 0
                let rawRate = Number(c['Rental Rate'] || c.rental_rate || 0);
                if (rawRate === 0 && fleetRateMap[plateKey]) {
                    rawRate = fleetRateMap[plateKey]; // FALLBACK TO FLEET RATE
                }

                const type = GET_CONTRACT_TYPE(bookingNo, fullContractNo, dateObj, dDateObj, normalizedBranch, branch, c);
                // The employee who opened the contract in Ejar (display name from Settings > Staff Mappings)
                const createdByRaw = String(c['Created By'] || c.created_by || c.CreatedBy || '').trim();
                const createdBy = NORMALIZE_STAFF_NAME(createdByRaw, staffMappings);

                // --- MODEL PRICING LOGIC ---
                // If a standard rate exists for this model (and YEAR), use it for Financial Calculations.
                let standardDailyRevenue = 0;
                let standardMonthlyRevenue = 0;
                let usedStandardRate = false;

                // Priority: Fleet Standard Name > Contract Name
                let modelRaw = fleetModelMap[plateKey];
                if (!modelRaw) {
                    modelRaw = (c.model || c['Model'] || '').trim().toUpperCase();
                }
                // Triangulate Year: Fleet Map > Contract > N/A
                let carYear = fleetYearMap[plateKey];
                if (!carYear) carYear = (c.year || c.Year || '').toString().trim();
                if (!carYear) carYear = 'N/A';

                // Construct Key: "MODEL - YEAR"
                const pricingKey = `${modelRaw} - ${carYear}`;
                
                // A model price counts only when it is set (> 0): 69 of the 72 saved entries were 0 (Sep 2026)
                const std = modelPricingMap[pricingKey] || {};
                standardDailyRevenue = Number(std.daily) || 0;
                standardMonthlyRevenue = Number(std.monthly) || 0;

                // The car's own rate from Ejar is daily for some cars (115 / 150 / 175) and monthly for others
                // (1,400 / 2,100 / 2,700): 500 AED or more is a month, so a day is a 30th of it
                const carDaily = rawRate >= 500 ? rawRate / 30 : rawRate;

                // Revenue per day: the model price first, the car's Ejar rate next
                const isMonthlyType = type === 'Monthly' || type === 'Leasing' || type === 'Invygo';
                let revenueBasis = 0;
                if (isMonthlyType && standardMonthlyRevenue > 0) {
                    revenueBasis = standardMonthlyRevenue / 30;
                    usedStandardRate = true;
                } else if (!isMonthlyType && standardDailyRevenue > 0) {
                    revenueBasis = standardDailyRevenue;
                    usedStandardRate = true;
                } else if (carDaily > 0) {
                    revenueBasis = carDaily;
                } else if (standardDailyRevenue > 0 || standardMonthlyRevenue > 0) {
                    revenueBasis = standardDailyRevenue || standardMonthlyRevenue / 30;
                    usedStandardRate = true;
                }

                return {
                    id: c.id || fullContractNo,
                    contractNo: fullContractNo,
                    bookingNumber: bookingNo,
                    customer: c.customer || c['Customer'],
                    branch: normalizedBranch,
                    city: BRANCH_TO_CITY(branch),
                    carOwner: c['Car Owner'] || 'Yelo',
                    plateNo: c['Plate No.'] || c.plate_no,
                    model: c.model || c['Model'],
                    pickupDate: rawPickupDate || pickupDate,
                    dropoffDate: rawDropoffDate || dropoffDate,
                    pickupAt: dateObj ? dateObj.getTime() : null,
                    dropoffAt: dDateObj ? dDateObj.getTime() : null,
                    operationStatus: RAW_STATUS_MAP(statusRaw),
                    type: type,
                    category: c['Car Category'] || c.car_category || 'Standard',
                    createdBy: createdBy,
                    createdByRaw,
                    year: Number(carYear) || (dateObj ? dateObj.getFullYear() : 'N/A'),
                    monthNum: dateObj ? dateObj.getMonth() + 1 : 'N/A',
                    quarter: getQuarter(pickupDate),
                    rentalRate: rawRate,
                    // Financial Normalization
                    normalizedDailyRevenue: revenueBasis,
                    isStandardRate: usedStandardRate,
                    hasRate: revenueBasis > 0
                };
            });
        } catch (error) {
            // thrown, not an empty list: a failed read used to show "0 open contracts" as if true (audit MON-20)
            logger.error('Error fetching UnifiedOpsView:', error);
            throw error;
        }
    },

    getUnifiedFleetView: async (forceRefresh = false) => {
        try {
            const [fleet, statusResult] = await Promise.all([
                fleetService.getAllCars(forceRefresh),
                maintenanceService.getStatusMap()
            ]);
            
            const statusMap = statusResult?.map || {};
            const normalizePlate = (p) => String(p || '').trim().toLowerCase().replace(/\s+/g, "");

            // The KPI fleet = the Fleet page's Total Fleet: the fleet's one rule (on the RTA list, not sold /
            // stolen / Ready To Sell). It had a rule of its own that also dropped "out of service" and kept
            // cars off the RTA list (audit DUP-03).
            return fleet
                .filter((f) => inFleet(f))
                .map(f => {
                    const plate = normalizePlate(f.plate_number || f.plate_no);
                    const realTimeStatus = statusMap[plate]?.status || f.status || 'available';
                    
                    return {
                        plateNo: f.plate_number || f.plate_no,
                        carCategory: f.car_category || f.category || 'Standard',
                        fleetStatus: realTimeStatus,
                        branch: NORMALIZE_BRANCH(f.branch || 'Head Office'),
                        city: BRANCH_TO_CITY(f.branch),
                        model: f.model_name || f.model,
                        make: f.make || f.brand_name || '',
                        year: f.year || f.car_year || '',
                        owner: f.owner || 'Yelo',
                        registrationExpiry: f.registration_expiry || '',
                        maintenanceDays: 0
                    };
                });
        } catch (error) {
            logger.error('Error fetching UnifiedFleetView:', error);
            throw error;
        }
    },

    getEmployeeTargets: async () => {
        try {
            if (USE_CONTRACTS_V2) return (await finesService.officeKpiAll())?.items || {};
            const querySnapshot = await getDocs(collection(db, SETTINGS_COLLECTION));
            const targets = {};
            querySnapshot.forEach(doc => {
                targets[doc.id] = doc.data();
            });
            return targets;
        } catch (error) {
            logger.error('Error fetching employee targets:', error);
            return {};
        }
    },

    saveEmployeeTarget: async (employeeName, targetData) => {
        try {
            if (USE_CONTRACTS_V2) {
                await storeKpiSave(employeeName, { ...targetData }, true);
                return { success: true };
            }
            const docRef = doc(db, SETTINGS_COLLECTION, employeeName);
            await setDoc(docRef, {
                ...targetData,
                last_updated: new Date().toISOString()
            }, { merge: true });
            return { success: true };
        } catch (error) {
            logger.error('Error saving employee target:', error);
            return { success: false, error: error.message };
        }
    },

    saveAllEmployeeTargets: async (allTargets) => {
        try {
            if (USE_CONTRACTS_V2) {
                // one transaction on the server (the batch's all or nothing); an empty save writes nothing
                if (Object.keys(allTargets || {}).length > 0) {
                    await finesService.officeKpiBatch(allTargets, { merge: true }).catch((e) => { throw officeError(e); });
                }
                return { success: true };
            }
            const batch = writeBatch(db);
            const now = new Date().toISOString();
            
            Object.entries(allTargets).forEach(([name, data]) => {
                const docRef = doc(db, SETTINGS_COLLECTION, name);
                batch.set(docRef, {
                    ...data,
                    last_updated: now
                }, { merge: true });
            });
            
            await batch.commit();
            return { success: true };
        } catch (error) {
            logger.error('Error batch saving employee targets:', error);
            return { success: false, error: error.message };
        }
    },

    getMaintenanceLogs: async (forceRefresh = false) => {
        try {
            return await maintenanceService.getAllLogs(forceRefresh);
        } catch (error) {
            logger.error('Error fetching maintenance logs:', error);
            return [];
        }
    },

    getDistinctStaffNames: async () => {
        try {
            const contracts = await kpiService.getUnifiedOpsView();
            const names = new Set();
            contracts.forEach(c => {
                if (c.createdBy) names.add(c.createdBy);
            });
            return Array.from(names).sort();
        } catch (error) {
            logger.error('Error fetching distinct staff names:', error);
            return [];
        }
    },

    getStaffMappings: async () => {
        try {
            if (USE_CONTRACTS_V2) return await storeKpiDoc(STAFF_MAPPINGS_DOC);
            const docSnap = await getDocs(query(collection(db, SETTINGS_COLLECTION), where('__name__', '==', STAFF_MAPPINGS_DOC)));
            
            if (docSnap.empty) return {};
            return docSnap.docs[0].data() || {};
        } catch (error) {
            logger.error('Error fetching staff mappings:', error);
            return {};
        }
    },

    saveStaffMappings: async (mappings) => {
        try {
            if (USE_CONTRACTS_V2) {
                await storeKpiSave(STAFF_MAPPINGS_DOC, { ...mappings }, false);
                return { success: true };
            }
            const docRef = doc(db, SETTINGS_COLLECTION, STAFF_MAPPINGS_DOC);
            await setDoc(docRef, {
                ...mappings,
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error('Error saving staff mappings:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Fetches raw (un-normalized) staff names from data sources
     */
    getRawStaffNames: async () => {
        try {
            // the contracts store: the names on the contracts of the last 12 months (never five years of contracts)
            const since = USE_CONTRACTS_V2 ? new Date(Date.now() - 365 * 86400000 + 4 * 3600000).toISOString().slice(0, 10) : undefined;
            const [regularRaw, airportRaw] = await Promise.all([
                contractsService.getAllContracts(false, { since }),
                airportService.getAllAirportContracts(false, { since })
            ]);

            const names = new Set();
            regularRaw.forEach(c => {
                const name = c['Created By'] || c.created_by || c.CreatedBy;
                if (name) names.add(name.toString().trim());
            });
            airportRaw.forEach(c => {
                const name = c['Created By'] || c.created_by || c.CreatedBy;
                if (name) names.add(name.toString().trim());
            });

            return Array.from(names).sort();
        } catch (error) {
            logger.error('Error fetching raw staff names:', error);
            return [];
        }
    },

    getStaffBranches: async () => {
        try {
            if (USE_CONTRACTS_V2) return await storeKpiDoc(STAFF_BRANCHES_DOC);
            const docSnap = await getDocs(query(collection(db, SETTINGS_COLLECTION), where('__name__', '==', STAFF_BRANCHES_DOC)));
            
            if (docSnap.empty) return {};
            return docSnap.docs[0].data() || {};
        } catch (error) {
            logger.error('Error fetching staff branches:', error);
            return {};
        }
    },

    saveStaffBranches: async (branches) => {
        try {
            if (USE_CONTRACTS_V2) {
                await storeKpiSave(STAFF_BRANCHES_DOC, { ...branches }, false);
                return { success: true };
            }
            const docRef = doc(db, SETTINGS_COLLECTION, STAFF_BRANCHES_DOC);
            await setDoc(docRef, {
                ...branches,
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error('Error saving staff branches:', error);
            return { success: false, error: error.message };
        }
    },

    getStaffRoles: async () => {
        try {
            if (USE_CONTRACTS_V2) return await storeKpiDoc(STAFF_ROLES_DOC);
            const docSnap = await getDocs(query(collection(db, SETTINGS_COLLECTION), where('__name__', '==', STAFF_ROLES_DOC)));
            
            if (docSnap.empty) return {};
            return docSnap.docs[0].data() || {};
        } catch (error) {
            logger.error('Error fetching staff roles:', error);
            return {};
        }
    },

    saveStaffRoles: async (roles) => {
        try {
            if (USE_CONTRACTS_V2) {
                await storeKpiSave(STAFF_ROLES_DOC, { ...roles }, false);
                return { success: true };
            }
            const docRef = doc(db, SETTINGS_COLLECTION, STAFF_ROLES_DOC);
            await setDoc(docRef, {
                ...roles,
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error('Error saving staff roles:', error);
            return { success: false, error: error.message };
        }
    },

    getStaffNos: async () => {
        try {
            if (USE_CONTRACTS_V2) return await storeKpiDoc(STAFF_NOS_DOC);
            const docSnap = await getDocs(query(collection(db, SETTINGS_COLLECTION), where('__name__', '==', STAFF_NOS_DOC)));
            
            if (docSnap.empty) return {};
            return docSnap.docs[0].data() || {};
        } catch (error) {
            logger.error('Error fetching staff numbers:', error);
            return {};
        }
    },

    saveStaffNos: async (nos) => {
        try {
            if (USE_CONTRACTS_V2) {
                await storeKpiSave(STAFF_NOS_DOC, { ...nos }, false);
                return { success: true };
            }
            const docRef = doc(db, SETTINGS_COLLECTION, STAFF_NOS_DOC);
            await setDoc(docRef, {
                ...nos,
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error('Error saving staff numbers:', error);
            return { success: false, error: error.message };
        }
    },

    /** Names (as in Ejar, or their display name) not counted as employees: people who left, managers who
     *  do not open contracts. Their contracts count for the branch the contract was opened in. */
    getStaffExcluded: async () => {
        try {
            if (USE_CONTRACTS_V2) {
                const names = (await storeKpiDoc(STAFF_EXCLUDED_DOC)).names;
                return Array.isArray(names) ? names : [];
            }
            const snap = await getDocs(query(collection(db, SETTINGS_COLLECTION), where('__name__', '==', STAFF_EXCLUDED_DOC)));
            if (snap.empty) return [];
            const names = (snap.docs[0].data() || {}).names;
            return Array.isArray(names) ? names : [];
        } catch (error) {
            logger.error('Error fetching excluded staff:', error);
            return [];
        }
    },

    saveStaffExcluded: async (names) => {
        try {
            const unique = Array.from(new Set((names || []).map((n) => String(n).trim()).filter(Boolean)));
            if (USE_CONTRACTS_V2) {
                await storeKpiSave(STAFF_EXCLUDED_DOC, { names: unique }, false);
                return { success: true };
            }
            await setDoc(doc(db, SETTINGS_COLLECTION, STAFF_EXCLUDED_DOC), {
                names: unique,
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error('Error saving excluded staff:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Expose the local normalization function for components
     */
    getNormalizedStaffName: (name) => NORMALIZE_STAFF_NAME(name)
};
